import { ArrowRightIcon, ColumnsIcon, Trash2Icon } from 'lucide-react'

import { Button } from '@/components/ui'
import { useDialogActions } from '@/stores'
import { DeleteDuplicatePagesDialog } from '../dialogs/DeleteDuplicatePagesDialog'
import { periodLabel } from '../reconciliation/helpers'
import { DuplicateBadge } from './DuplicateBadge'
import { DuplicateCompareWorkspace } from './DuplicateCompareWorkspace'
import {
  confidenceLabel,
  type DuplicatePageGroup,
  originalLocationLabel,
  pageRangeLabel,
  similarityLabel,
} from './helpers'

// One detected repeat, with a Delete action that removes the copy (the original always stays).
export const DuplicateGroupCard = ({
  group,
}: {
  group: DuplicatePageGroup
}) => {
  const { openDialog } = useDialogActions()
  const { statement, duplicate, original } = group
  const similarity = similarityLabel(duplicate.duplicateSimilarity)

  return (
    <div className="rounded-lg border bg-white px-6 py-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <DuplicateBadge />
            <span className="truncate text-sm font-medium text-foreground">
              {statement.fileName}
            </span>
          </div>

          <p className="flex flex-wrap items-center gap-1.5 text-sm text-foreground">
            <span className="font-medium">{pageRangeLabel(duplicate)}</span>
            <ArrowRightIcon className="size-3.5 text-muted-foreground" />
            <span className="text-muted-foreground">
              repeat {originalLocationLabel(group)}
              {original && ' (original)'}
            </span>
          </p>

          <p className="text-xs text-muted-foreground">
            {periodLabel(duplicate)},{' '}
            {confidenceLabel(duplicate.duplicateConfidence)}
            {similarity && `, ${similarity}`}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            theme="outline"
            size="sm"
            className="cursor-pointer"
            disabled={!statement.fileUrl}
            onClick={() =>
              openDialog(DuplicateCompareWorkspace.id, {
                statementId: statement.id,
                duplicateSegmentId: duplicate.id,
              })
            }
          >
            <ColumnsIcon />
            Compare
          </Button>
          <Button
            theme="outline"
            size="sm"
            className="cursor-pointer text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={() => openDialog(DeleteDuplicatePagesDialog.id, [group])}
          >
            <Trash2Icon />
            Delete copy
          </Button>
        </div>
      </div>
    </div>
  )
}
